/**
 * 高危集群变更确认层：列出 actions → 登录密码 → 确认短语「确认执行」。
 * 样式复用 sbx-approval-*（与沙箱审批一致）。
 * 数据流：OpsWorkspace 调 requestMutationApproval → 本组件订阅 store 弹出 →
 *   settleMutationApproval 把密码+短语 resolve 回调用方 → POST /api/ops/mutation/execute。
 */
import { useEffect, useState } from "react";
import { Button, Input, Typography } from "antd";
import {
  getPendingMutation,
  settleMutationApproval,
  subscribeMutationApproval,
} from "../../ops/mutationApprovalStore";

export function OpsMutationSheet() {
  const [tick, setTick] = useState(0);
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");

  // store 是 React 外单例：订阅后用 tick 强制重渲以读到最新 pending
  useEffect(() => subscribeMutationApproval(() => setTick((x) => x + 1)), []);

  const pending = getPendingMutation();
  void tick; // tick 仅为触发重渲而存在，值本身不读

  useEffect(() => {
    if (!pending) {
      setPassword("");
      setPhrase("");
    }
  }, [pending]);

  // 捕获阶段监听 Esc = 取消；无 pending 时不挂监听
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        settleMutationApproval(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pending]);

  if (!pending) return null;
  const { plan } = pending;
  // 双保险：密码非空 + 确认短语逐字等于「确认执行」才放行提交
  const canSubmit =
    Boolean(password) && phrase.trim() === "确认执行";

  return (
    <div
      className="sbx-approval-overlay"
      role="presentation"
      onClick={() => settleMutationApproval(false)}
    >
      <div
        className="sbx-approval-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ops-mutate-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sbx-approval-head">
          <div>
            <Typography.Text
              id="ops-mutate-title"
              strong
              className="sbx-approval-title"
            >
              确认集群变更（管理员）
            </Typography.Text>
            <Typography.Paragraph type="secondary" className="sbx-approval-meta">
              计划 {plan.planId}
              {plan.targetId ? ` · 目标 ${plan.targetId}` : ""}
            </Typography.Paragraph>
          </div>
          <span className="sbx-approval-badge">风险 {plan.risk}</span>
        </div>

        <ul style={{ margin: "0 0 12px", paddingLeft: 20, fontSize: 13 }}>
          {plan.actions.map((a, i) => (
            <li key={i}>
              [{a.op}] {a.namespace || "-"}/{a.resource} — {a.summary}
            </li>
          ))}
        </ul>

        {plan.analysisExcerpt ? (
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 3 }}
            style={{ fontSize: 12 }}
          >
            {plan.analysisExcerpt}
          </Typography.Paragraph>
        ) : null}

        <Input.Password
          placeholder="再次输入登录密码"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          style={{ marginBottom: 8 }}
        />
        <Input
          placeholder='请输入：确认执行'
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
        />

        <div className="sbx-approval-actions" style={{ marginTop: 12 }}>
          <Button onClick={() => settleMutationApproval(false)}>取消 Esc</Button>
          <Button
            type="primary"
            danger
            disabled={!canSubmit}
            onClick={() =>
              settleMutationApproval(true, {
                password,
                confirmPhrase: phrase.trim(),
              })
            }
          >
            授权执行
          </Button>
        </div>
      </div>
    </div>
  );
}
